import React from 'react';
import { Box, Flex, Text, Button, VStack, HStack } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useApp } from '../hooks/useApp';
import { TagBadge, ArticleCardMini } from '../components/ui/Cards';

export default function ArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { articles } = useApp();

  const article = articles.find(a => a.id === id);
  const related = articles.filter(a => a.id !== id && a.category === article?.category).slice(0, 3);

  if (!article) {
    return (
      <Box pt="100px" pb="90px" px="5%" textAlign="center">
        <Text fontSize="3rem" mb={3}>😕</Text>
        <Text fontSize="1.2rem" mb={6}>Article not found.</Text>
        <Button
          bg="#00c3ff"
          color="#000"
          fontWeight={700}
          onClick={() => navigate('/articles')}
        >
          ← Back to Articles
        </Button>
      </Box>
    );
  }

  return (
    <Box pt="100px" pb="90px" px="5%">
      <Box maxW="800px" mx="auto">
        {/* Back */}
        <Button
          variant="ghost"
          leftIcon={<ArrowBackIcon />}
          onClick={() => navigate('/articles')}
          color="#6b7fa0"
          mb={6}
          px={0}
          _hover={{ color: '#00c3ff', bg: 'transparent' }}
        >
          Back to Articles
        </Button>

        {/* Header image */}
        <Box
          h={{ base: '200px', md: '320px' }}
          background={article.gradient}
          borderRadius="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="5rem"
          mb={8}
        >
          {article.emoji}
        </Box>

        {/* Meta */}
        <Flex gap={2} flexWrap="wrap" mb={4}>
          <TagBadge tag={article.category} />
          {article.tags?.map(tag => <TagBadge key={tag} tag={tag} />)}
        </Flex>

        <Text
          as="h1"
          fontFamily="'Fraunces', serif"
          fontSize={{ base: '2rem', md: '2.8rem' }}
          fontWeight={900}
          letterSpacing="-0.02em"
          lineHeight={1.2}
          mb={4}
        >
          {article.title}
        </Text>

        <Flex align="center" gap={4} color="#6b7fa0" fontSize="0.85rem" mb={8}>
          <Text>{article.date}</Text>
          <Box w="1px" h="14px" bg="rgba(255,255,255,0.15)" />
          <Text>⏱ {article.readTime} read</Text>
          <Box w="1px" h="14px" bg="rgba(255,255,255,0.15)" />
          <Text>📁 {article.category}</Text>
        </Flex>

        {/* Content */}
        <Box
          sx={{
            'h2': {
              fontFamily: "'Fraunces', serif",
              fontSize: '1.5rem',
              fontWeight: 700,
              mt: 8,
              mb: 4,
              color: '#f0f0f5',
            },
            'h3': {
              fontFamily: "'Fraunces', serif",
              fontSize: '1.2rem',
              fontWeight: 700,
              mt: 6,
              mb: 3,
              color: '#47c8ff',
            },
            'p': {
              color: '#b8b8d0',
              lineHeight: 1.85,
              mb: 4,
              fontSize: '1.02rem',
            },
            'blockquote': {
              borderLeft: '3px solid #00c3ff',
              pl: 4,
              py: 2.5,
              bg: 'rgba(0,195,255,0.04)',
              borderRadius: '0 6px 6px 0',
              my: 6,
              color: '#6b7fa0',
            },
            'a': {
              color: '#47c8ff',
              textDecoration: 'underline',
            },
            'ul, ol': {
              pl: 6,
              mb: 4,
            },
            'li': {
              color: '#b8b8d0',
              lineHeight: 1.8,
              mb: 2,
            },
          }}
          dangerouslySetInnerHTML={{ __html: article.content || '<p>Content coming soon...</p>' }}
        />

        {/* Share */}
        <Box
          mt={10}
          pt={8}
          borderTop="1px solid rgba(255,255,255,0.07)"
        >
          <Text fontWeight={600} mb={4}>Share this article</Text>
          <Flex gap={3} flexWrap="wrap">
            {[
              { label: '🐦 Twitter', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${window.location.href}` },
              { label: '💼 LinkedIn', url: `https://linkedin.com/sharing/share-offsite/?url=${window.location.href}` },
              { label: '📱 WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(article.title + ' ' + window.location.href)}` },
            ].map(s => (
              <a key={s.label} href={s.url} target="_blank" rel="noreferrer">
                <Box
                  px={4}
                  py={2}
                  bg="#0e1520"
                  border="1px solid rgba(255,255,255,0.07)"
                  borderRadius="8px"
                  fontSize="0.85rem"
                  fontWeight={500}
                  transition="all 0.2s"
                  _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }}
                >
                  {s.label}
                </Box>
              </a>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Related */}
      {related.length > 0 && (
        <Box maxW="800px" mx="auto" mt={14}>
          <Text fontFamily="'Fraunces', serif" fontSize="1.4rem" fontWeight={700} mb={6}>
            Related <Text as="span" color="#00c3ff">Articles</Text>
          </Text>
          <VStack gap={4} align="stretch">
            {related.map(a => (
              <ArticleCardMini
                key={a.id}
                article={a}
                onClick={() => { navigate(`/articles/${a.id}`); window.scrollTo({ top: 0 }); }}
              />
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
